import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AuthenticationProvider } from '@shared/shared/enums/authentication-provider.enum';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { IUser } from '@shared/shared/interfaces/user.interfaces';
import { LanguageService } from '@shared/shared/services/language-service';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import * as UserStore from '../store/user';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  public user$: Observable<IUser> = this.ngrxStore.select(UserStore.getUserFeatureState);

  constructor(
    private router: Router,
    private ngrxStore: Store<UserStore.UserState>,
    private siteSettingsService: SiteSettingsService,
    private languageService: LanguageService
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> {
    return combineLatest([this.user$.pipe(take(1)), this.siteSettingsService.fetchSiteSettings()]).pipe(
      map(([user, siteSettings]: [IUser, ISiteSettings]) => {
        if (!route.url || !route.url.length) {
          this.router.navigate(['']);
          return user.isAuthenticated;
        }

        const routeUrlPath = route.url[0].path;
        if (user.isAuthenticated) {
          if (routeUrlPath === 'home') {
            this.router.navigate([''], { queryParams: { returnUrl: routerState.url } });
          }
          if (routeUrlPath === 'landing' && siteSettings.fe_configuration.path === 'gtc') {
            this.router.navigate([siteSettings.fe_configuration.root_route]);
          }
        } else {
          if (
            siteSettings?.authentication_provider.provider === AuthenticationProvider.visa_lac ||
            siteSettings?.authentication_provider.provider === AuthenticationProvider.creditsaison ||
            siteSettings?.authentication_provider.provider === AuthenticationProvider.creditsaisondiamond
          ) {
            const language = this.languageService.preparingSiteLanguageAttribute(siteSettings);
            // HACK: returnUrl magically changes to undefined during redirects, so we save it to session storage
            sessionStorage.setItem('returnUrl', routerState.url);
            localStorage.setItem(
              'auth guard redirect ',
              `${window.location.origin}${siteSettings.authentication_provider.login_url}?onwardUrl=${routerState.url}&lang=${language}`
            );
            window.location.href = `${window.location.origin}${siteSettings.authentication_provider.login_url}?onwardUrl=${routerState.url}&lang=${language}`;
          } else {
            switch (routeUrlPath) {
              case 'home':
                this.router.navigate(['/home'], { queryParamsHandling: 'preserve' });
                break;
              case 'travel':
                sessionStorage.setItem('returnUrl', routerState.url);
                if (routerState.url === '/travel') {
                  this.router.navigate(['/home'], { queryParams: { returnUrl: routerState.url } });
                } else {
                  // hotel details page direct link from email
                  this.router.navigate(['/user/login'], { queryParams: { returnUrl: routerState.url } });
                }
                break;
              case 'landing':
                this.router.navigate(['/home']);
                break;
              default:
                this.router.navigate(['/user/login'], { queryParams: { returnUrl: routerState.url } });
            }
          }
        }

        return user.isAuthenticated;
      })
    );
  }
}
